import React from 'react';
import { Sparkles, MoreHorizontal, Link2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
export interface ConceptCardProps {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  sourceHook: string;
  category: string;
  format: string;
  status: 'draft' | 'ready' | 'published';
  dateCreated: string;
  onViewDetails?: (id: string) => void;
}
export function ConceptCard({
  id,
  title,
  description,
  thumbnail,
  sourceHook,
  category,
  format,
  status,
  dateCreated,
  onViewDetails
}: ConceptCardProps) {
  const statusConfig = {
    draft: {
      color: 'bg-gray-100 text-gray-700',
      label: 'Draft'
    },
    ready: {
      color: 'bg-green-100 text-green-700',
      label: 'Ready'
    },
    published: {
      color: 'bg-blue-100 text-blue-700',
      label: 'Published'
    }
  };
  return <Card className="group overflow-hidden border-gray-200 hover:shadow-lg transition-all duration-300 bg-white h-full flex flex-col" data-id="element-210">
      {/* Thumbnail Section */}
      <div className="relative aspect-video bg-gray-100 overflow-hidden" data-id="element-211">
        <img src={thumbnail} alt={title} className="w-full h-full object-cover" data-id="element-212" />
        {/* Status Badge Overlay */}
        <div className="absolute top-2 right-2" data-id="element-213">
          <Badge className={`${statusConfig[status].color} border-none font-medium shadow-md`} data-id="element-214">
            <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-215" />
            {statusConfig[status].label}
          </Badge>
        </div>
      </div>

      <CardContent className="p-5 flex-1 flex flex-col" data-id="element-216">
        {/* Header with Icon */}
        <div className="flex justify-between items-start gap-2 mb-3" data-id="element-217">
          <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0" data-id="element-218">
            <Sparkles className="h-5 w-5 text-purple-600" data-id="element-219" />
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6 -mr-2 text-gray-400 hover:text-gray-600" data-id="element-220">
            <MoreHorizontal className="h-4 w-4" data-id="element-221" />
          </Button>
        </div>

        {/* Category & Format */}
        <div className="flex gap-2 mb-3" data-id="element-222">
          <Badge variant="secondary" className="bg-purple-500 text-white hover:bg-purple-600 border-none font-medium px-2.5 w-fit" data-id="element-223">
            {category}
          </Badge>
          <Badge variant="outline" className="text-xs font-normal text-gray-600 border-gray-200 bg-gray-50" data-id="element-224">
            {format}
          </Badge>
        </div>

        {/* Title & Description */}
        <h3 className="font-bold text-lg leading-tight text-gray-900 mb-2 line-clamp-2" data-id="element-225">
          {title}
        </h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-1" data-id="element-226">
          {description}
        </p>

        {/* Source Hook Link */}
        <div className="flex items-center gap-2 mb-4 text-xs text-gray-500" data-id="element-227">
          <Link2 className="h-3.5 w-3.5" data-id="element-228" />
          <span className="truncate" data-id="element-229">From hook: {sourceHook}</span>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto" data-id="element-230">
          <span className="text-xs text-gray-500" data-id="element-231">{dateCreated}</span>
          <Button size="sm" onClick={() => onViewDetails?.(id)} className="bg-[#139191] hover:bg-[#139191]/90 text-white text-xs" data-id="element-232">
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>;
}